import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Alert,
	AlertDescription,
	AlertIcon,
	Badge,
	Box,
	Button,
	Checkbox,
	Flex,
	HStack,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Radio,
	RadioGroup,
	Skeleton,
	Stack,
	Text,
} from '@chakra-ui/react';
import {
	useEmailVerificationLimits,
	useVerificationCreditSummary,
} from '../../../hooks/useVerificationCredits';
import { useVerifyLeadContacts } from '../../../hooks/CRM/useOutreach';
import { emailVerificationOf } from './leadMeta';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const CheckBadge = ({ contact }) => {
	const check = emailVerificationOf(contact);
	if (!check) {
		return (
			<Badge colorScheme='gray' variant='outline'>
				Not checked
			</Badge>
		);
	}
	return <Badge colorScheme={check.color}>{check.label}</Badge>;
};

/**
 * Pick which contact emails to check and which verification service to use, then show the results.
 * `contactIds` preselects contacts; null preselects every contact with an email. Mount it only
 * while open so each opening starts fresh.
 */
const VerifyEmailsModal = ({
	isOpen,
	onClose,
	lead,
	companyId,
	contactIds,
}) => {
	const limits = useEmailVerificationLimits(companyId);
	const credits = useVerificationCreditSummary(companyId);
	const verify = useVerifyLeadContacts(companyId);

	const withEmail = useMemo(
		() => (lead?.contacts || []).filter((c) => c.email),
		[lead?.contacts],
	);
	const [selected, setSelected] = useState(
		() => contactIds || withEmail.map((c) => c._id),
	);
	const [provider, setProvider] = useState('');
	const [checkedIds, setCheckedIds] = useState(null);
	const [error, setError] = useState('');

	const providers = useMemo(() => limits.data?.providers || [], [limits.data]);
	useEffect(() => {
		if (!isOpen || !limits.data) return;
		setProvider((current) =>
			providers.some((p) => p.id === current)
				? current
				: limits.data.selectedProvider || providers[0]?.id || '',
		);
	}, [isOpen, limits.data, providers]);

	const chosen = providers.find((p) => p.id === provider);
	const isFree = Boolean(chosen?.freeForCompany);
	const cost = isFree ? 0 : selected.length;
	const balance = credits.data?.balance;
	const notEnough = !isFree && typeof balance === 'number' && balance < cost;
	const done = checkedIds !== null;

	const toggle = (id, on) =>
		setSelected((ids) =>
			on ? [...new Set([...ids, id])] : ids.filter((x) => x !== id),
		);

	const run = async () => {
		setError('');
		try {
			await verify.mutateAsync({ id: lead._id, contactIds: selected, provider });
			setCheckedIds(selected);
		} catch (err) {
			setError(err.message);
		}
	};

	const shown = done
		? withEmail.filter((c) => checkedIds.includes(c._id))
		: withEmail;

	return (
		<Modal
			isOpen={isOpen}
			onClose={verify.isPending ? () => {} : onClose}
			size='lg'
			scrollBehavior='inside'>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>
					{done ? 'Verification results' : 'Verify email addresses'}
					<Text fontSize='sm' fontWeight='normal' color='gray.500'>
						{lead?.name}
					</Text>
				</ModalHeader>
				{!verify.isPending && <ModalCloseButton />}
				<ModalBody>
					<Stack spacing={5}>
						{error && (
							<Alert status='error' borderRadius='md' fontSize='sm'>
								<AlertIcon />
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						)}

						{!done && (
							<Box>
								<Text fontWeight='semibold' fontSize='sm' mb={2}>
									Check with
								</Text>
								{limits.isLoading ? (
									<Stack>
										<Skeleton h='52px' borderRadius='md' />
										<Skeleton h='52px' borderRadius='md' />
									</Stack>
								) : !providers.length ? (
									<Alert status='warning' borderRadius='md' fontSize='sm'>
										<AlertIcon />
										<AlertDescription>
											No email verification service is set up on this platform yet.
										</AlertDescription>
									</Alert>
								) : (
									<RadioGroup value={provider} onChange={setProvider}>
										<Stack spacing={2}>
											{providers.map((p) => (
												<Box
													key={p.id}
													as='label'
													cursor='pointer'
													borderWidth='1px'
													borderColor={provider === p.id ? 'teal.400' : 'gray.200'}
													bg={provider === p.id ? 'teal.50' : 'white'}
													borderRadius='md'
													px={3}
													py={2.5}>
													<Flex align='center' gap={3}>
														<Radio value={p.id} colorScheme='teal' />
														<Box flex='1'>
															<HStack spacing={2}>
																<Text fontWeight='semibold' fontSize='sm'>
																	{p.label}
																</Text>
																{p.id === limits.data?.selectedProvider && (
																	<Badge colorScheme='gray' fontSize='2xs'>
																		Default
																	</Badge>
																)}
																{p.freeForCompany && (
																	<Badge colorScheme='green' fontSize='2xs'>
																		Free for you
																	</Badge>
																)}
															</HStack>
															{p.description && (
																<Text fontSize='xs' color='gray.500'>
																	{p.description}
																</Text>
															)}
														</Box>
													</Flex>
												</Box>
											))}
										</Stack>
									</RadioGroup>
								)}
							</Box>
						)}

						<Box>
							<Flex justify='space-between' align='center' mb={2}>
								<Text fontWeight='semibold' fontSize='sm'>
									{done ? 'Results' : 'Emails to check'}
								</Text>
								{!done && withEmail.length > 1 && (
									<Button
										size='xs'
										variant='ghost'
										onClick={() =>
											setSelected(
												selected.length === withEmail.length
													? []
													: withEmail.map((c) => c._id),
											)
										}>
										{selected.length === withEmail.length ? 'Clear all' : 'Select all'}
									</Button>
								)}
							</Flex>
							<Stack
								spacing={0}
								borderWidth='1px'
								borderColor='gray.100'
								borderRadius='md'>
								{shown.map((c, i) => {
									const check = emailVerificationOf(c);
									return (
										<Flex
											key={c._id}
											px={3}
											py={2}
											gap={3}
											align='center'
											borderTopWidth={i ? '1px' : 0}
											borderColor='gray.100'>
											{!done && (
												<Checkbox
													colorScheme='teal'
													isChecked={selected.includes(c._id)}
													onChange={(e) => toggle(c._id, e.target.checked)}
												/>
											)}
											<Box flex='1' minW={0}>
												<Text fontSize='sm' fontFamily='mono' noOfLines={1}>
													{c.email}
												</Text>
												{(c.name || (done && check?.subStatus)) && (
													<Text fontSize='xs' color='gray.500' noOfLines={1}>
														{[c.name, done ? check?.subStatus : '']
															.filter(Boolean)
															.join(' · ')}
													</Text>
												)}
											</Box>
											<CheckBadge contact={c} />
										</Flex>
									);
								})}
							</Stack>
						</Box>

						{!done && providers.length > 0 && (
							<Box fontSize='sm' color='gray.600'>
								{isFree ? (
									<Text>{chosen.label} checks are free for your company.</Text>
								) : (
									<Text>
										Uses {plural(cost, 'credit')}
										{typeof balance === 'number' &&
											` · ${plural(balance, 'credit')} available`}
										.
									</Text>
								)}
								{notEnough && (
									<Alert status='warning' borderRadius='md' fontSize='sm' mt={2}>
										<AlertIcon />
										<AlertDescription>
											Not enough credits.{' '}
											<Link as={RouterLink} to='/audiences/verify' color='teal.600'>
												Buy verification credits
											</Link>
										</AlertDescription>
									</Alert>
								)}
							</Box>
						)}
					</Stack>
				</ModalBody>
				<ModalFooter gap={2}>
					{done ? (
						<Button colorScheme='teal' onClick={onClose}>
							Done
						</Button>
					) : (
						<>
							<Button variant='ghost' onClick={onClose} isDisabled={verify.isPending}>
								Cancel
							</Button>
							<Button
								colorScheme='teal'
								onClick={run}
								isLoading={verify.isPending}
								loadingText='Checking…'
								isDisabled={!selected.length || !provider || notEnough}>
								Verify {plural(selected.length, 'email')}
							</Button>
						</>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default VerifyEmailsModal;
